import { styled } from '@linaria/react';
import { Table as MantineTable, Pagination, ScrollArea } from '@mantine/core';
import { type CSSProperties, type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { inkLight, secondaryBase, skyBase, skyDark, skyLightest } from '../../styles/design-tokens';
import { Icon } from '../../ui-library/icon/icon';
import { Text } from '../../ui-library/typography/typography';
import { DEFAULT_PAGE_SIZE } from './table.constants';

export type TableColumn<T extends object> = {
  key: keyof T;
  label: ReactNode;
  render?: (value: T[keyof T], row: T) => ReactNode;
  sortable?: boolean;
  sortValue?: (row: T) => number | string;
  width?: string;
};

type TableProps<T extends object> = {
  data: T[];
  columns: TableColumn<T>[];
  onRowClick?: (row: T) => void;
  selectedRowId?: string | number;
  getRowId: (row: T) => string | number;
  defaultSortedBy?: keyof T | null;
  defaultSortedByDirection?: 'asc' | 'desc';
  stickyHeader?: boolean;
  isChildRow?: (row: T) => boolean;
  getParentId?: (row: T) => string | number;
  highlightParentRows?: boolean;
  pageSize?: number;
  currentPage?: number;
  totalItems?: number;
  onPageChange?: (page: number) => void;
  customSort?: (data: T[], sortBy: keyof T, direction: 'asc' | 'desc') => T[];
  // When true, the caller owns pagination and `data` is assumed to already be
  // the current page's slice. The table skips its local `.slice()` step and
  // uses `totalItems` only to drive the pagination UI page-count math.
  serverSidePagination?: boolean;
  // Optional callback fired whenever the user toggles sort on a header.
  // Useful for pushing sort into a server-side `ordering` query param.
  onSortChange?: (sortBy: keyof T, direction: 'asc' | 'desc') => void;
};

function SortableTableHeader({
  children,
  reversed,
  sorted,
  onSort,
  sortable = true,
  style,
}: {
  children: ReactNode;
  reversed: boolean;
  sorted: boolean;
  onSort(): void;
  sortable?: boolean;
  style?: CSSProperties;
}) {
  return (
    <MantineTable.Th style={style}>
      <HeaderContent onClick={sortable ? onSort : undefined} sortable={sortable}>
        <Text weight="bold" size="regular">
          {children}
        </Text>
        {sortable && (
          <Icon
            name={sorted ? (reversed ? 'south' : 'north') : 'south'}
            color={sorted ? secondaryBase : skyDark}
            size={16}
            style={{
              cursor: 'pointer',
              opacity: sorted ? 1 : 0.5,
            }}
          />
        )}
      </HeaderContent>
    </MantineTable.Th>
  );
}

export function Table<T extends object>({
  data,
  columns,
  onRowClick,
  selectedRowId,
  getRowId,
  defaultSortedBy = null,
  defaultSortedByDirection = 'asc',
  stickyHeader = false,
  isChildRow = () => false,
  getParentId = () => '',
  highlightParentRows = false,
  pageSize = DEFAULT_PAGE_SIZE,
  currentPage = 1,
  totalItems = 0,
  onPageChange,
  customSort,
  serverSidePagination = false,
  onSortChange,
}: TableProps<T>) {
  const i18n = useTranslation();
  const [sortedBy, setSortedBy] = useState<keyof T | null>(defaultSortedBy);
  const [reverseSortDirection, setReverseSortDirection] = useState(defaultSortedByDirection === 'desc');

  const setSorting = (field: keyof T) => {
    const reversed = field === sortedBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortedBy(field);
    onSortChange?.(field, reversed ? 'desc' : 'asc');
  };

  const sortData = (data: T[]): T[] => {
    if (!sortedBy) {
      return data;
    }

    if (customSort) {
      return customSort(data, sortedBy, reverseSortDirection ? 'desc' : 'asc');
    }

    // Group parent rows with their children
    const parentRows = data.filter((row) => !isChildRow(row));
    const childrenByParent = data.reduce(
      (acc, row) => {
        if (isChildRow(row)) {
          const parentId = getParentId(row);
          if (!acc[parentId]) {
            acc[parentId] = [];
          }
          acc[parentId].push(row);
        }
        return acc;
      },
      {} as Record<string | number, T[]>,
    );

    // Sort parent rows
    const sortCol = columns.find((c) => c.key === sortedBy);
    const sortedParents = [...parentRows].sort((a, b) => {
      const aValue = sortCol?.sortValue ? sortCol.sortValue(a) : a[sortedBy];
      const bValue = sortCol?.sortValue ? sortCol.sortValue(b) : b[sortedBy];

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return reverseSortDirection ? -comparison : comparison;
      }

      // Return 0 for equal values so equal elements keep their input order
      // (stable sort); returning 1 here reorders equal rows unpredictably.
      const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      return reverseSortDirection ? -comparison : comparison;
    });

    // Reconstruct the array with children following their parents
    return sortedParents.flatMap((parent) => {
      const children = childrenByParent[getRowId(parent)] || [];
      return [parent, ...children];
    });
  };

  const sortedData = sortData(data);

  // When the caller handles pagination server-side, `data` is already one page
  // of results — don't slice it again. Otherwise apply local pagination to the
  // in-memory sorted data.
  const paginatedData =
    serverSidePagination || !pageSize
      ? sortedData
      : sortedData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <StyledTable stickyHeader={stickyHeader}>
      <ScrollArea style={{ flex: 1, minHeight: 0 }} scrollbarSize={0}>
        <MantineTable>
          <MantineTable.Thead
            style={{ position: stickyHeader ? 'sticky' : 'static', top: 0, backgroundColor: 'white', zIndex: 1 }}
          >
            <MantineTable.Tr>
              {columns.map((column) => (
                <SortableTableHeader
                  key={column.key.toString()}
                  sorted={sortedBy === column.key}
                  reversed={reverseSortDirection}
                  onSort={() => setSorting(column.key)}
                  sortable={column.sortable !== false}
                  style={{ width: column.width }}
                >
                  {column.label}
                </SortableTableHeader>
              ))}
            </MantineTable.Tr>
          </MantineTable.Thead>
          <MantineTable.Tbody>
            {paginatedData.map((row) => (
              <TableRow
                key={getRowId(row)}
                onClick={() => onRowClick?.(row)}
                data-selected={selectedRowId === getRowId(row)}
                data-child={isChildRow(row)}
                data-is-project={highlightParentRows && !isChildRow(row)}
                style={{ cursor: onRowClick ? 'pointer' : 'default' }}
              >
                {columns.map((column) => (
                  <MantineTable.Td key={column.key.toString()} style={{ width: column.width, padding: '4px 8px' }}>
                    {column.render ? column.render(row[column.key], row) : <Text>{row[column.key]?.toString()}</Text>}
                  </MantineTable.Td>
                ))}
              </TableRow>
            ))}
          </MantineTable.Tbody>
        </MantineTable>
      </ScrollArea>
      <PaginationContainer>
        <Text size="tiny" color={skyBase}>
          {i18n.t('common.table_pagination_summary', {
            start: totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1,
            end: Math.min(currentPage * pageSize, totalItems),
            total: totalItems,
          })}
        </Text>
        {totalItems > pageSize && (
          <Pagination
            total={Math.ceil(totalItems / pageSize)}
            value={currentPage}
            onChange={onPageChange}
            size="sm"
            siblings={1}
            boundaries={1}
          />
        )}
      </PaginationContainer>
    </StyledTable>
  );
}

const HeaderContent = styled.div<{ sortable?: boolean }>`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 4px;
  padding: 8px 0px;
  margin: 0;
  text-align: left;
  border-radius: 8px;
  cursor: ${(props) => (props.sortable ? 'pointer' : 'default')};
`;

const StyledTable = styled.div<{ stickyHeader?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 100%;

  .mantine-Table-table thead tr th {
    transition: background-color 0.2s ease;
    background-color: transparent;
    padding: 0px 8px;
  }
`;

const TableRow = styled(MantineTable.Tr)`
  height: 48px;

  &:hover {
    background-color: ${skyLightest};
  }

  &[data-selected='true'] {
    background-color: ${skyLightest};
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 36px;
  gap: 8px;
  position: sticky;
  bottom: 0;
  background-color: white;
  z-index: 1;
  padding-top: 8px;

  .mantine-Pagination-root {
    .mantine-Pagination-control {
      border-radius: 32px;
      padding: 6px 12px;
      margin: 0 2px;
      cursor: pointer;

      &[data-active] {
        background-color: ${secondaryBase};
        border-color: ${secondaryBase};
        color: white;
      }

      &:not([data-active]) {
        color: ${inkLight};
      }

      &:not([data-active]):hover {
        background-color: ${skyLightest};
      }
    }
  }
`;
